import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useGenerationCosts } from '../hooks/useGenerationCosts';
import { ImageSelector } from '../components/ImageSelector';
import { AllGenerationsViewer } from '../components/AllGenerationsViewer';
import { GenerationLoadingOverlay, LOADING_SEQUENCES } from '../components/GenerationLoadingOverlay';
import { CloudflareR2Service } from '../lib/cloudflareR2Service';
import ReactBeforeSliderComponent from 'react-before-after-slider-component';
import 'react-before-after-slider-component/dist/build.css';
import { OptimizedImage, ImageOptimizer } from '../lib/imageOptimizer';
import { tokenService } from '../lib/tokenService';
import { userImageService, UserImage } from '../lib/userImageService';
import { userGalleryService } from '../lib/userGalleryService';
import { aiTouchupService } from '../lib/aiTouchupService';
import { furnitureRemovalService } from '../lib/furnitureRemovalService';

const cleanupBlobUrl = (url: string | null) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

export const AITouchup: React.FC = () => {
  const { user, checkTokenBalance, refreshTokenBalance, updateUserTokens, isUpdatingTokens } = useAuth();
  const { costs } = useGenerationCosts();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const resultSectionRef = useRef<HTMLDivElement>(null);
  const operationSectionRef = useRef<HTMLDivElement>(null);
  const isSubmittingRef = useRef(false);
  
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [optimizedImage, setOptimizedImage] = useState<OptimizedImage | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [brushSize, setBrushSize] = useState(20);
  const [isDrawing, setIsDrawing] = useState(false);
  const [maskData, setMaskData] = useState<ImageData | null>(null);
  const [operationType, setOperationType] = useState<'erase' | 'change' | 'clean_selected'>('erase');
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<React.ReactNode | null>(null);
  const [resultImage, setResultImage] = useState<string | null>(null);
  const [lastProcessingId, setLastProcessingId] = useState<string>('');
  
  // Mobile detection
  const isMobile = useMemo(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }, []);
  
  // Get dynamic token cost for AI touchup
  const TOKENS_REQUIRED = costs.ai_touchup || 8; // Fallback to 8 if not loaded yet

  const currentTokenBalance = useMemo(() => user?.tokens || 0, [user?.tokens]);

  const handleImageSelected = async (file: File, source: 'upload' | 'existing', imageData?: UserImage) => {
    // Reset state
    setError(null);
    setSuccess(false);
    setOptimizedImage(null);
    cleanupBlobUrl(resultImage);
    setResultImage(null);
    setLastProcessingId('');
    clearMask();

    setSelectedImage(file);
    setLoading(true);

    try {
      // Handle CORS workaround files
      let actualFile = file;
      if ((file as any).isCorsWorkaround) {
        console.log('🔄 Detected CORS workaround file, getting actual image data...');
        actualFile = await userImageService.getImageDataForWorkaround(file);
      }

      const isHeicFile = actualFile.name.toLowerCase().endsWith('.heic') || actualFile.name.toLowerCase().endsWith('.heif');

      if (isHeicFile) {
        try {
          const r2Service = CloudflareR2Service.fromEnvironment();
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const uploadResult = await r2Service.uploadRawFileForProcessing(actualFile, { userId: user?.id });

          // HERE: Trigger backend processing and wait for the result
          // This is a placeholder for the actual server-side call
          // const convertedImageUrl = await triggerBackendConversion(uploadResult.key);

          // For now, we will simulate a delay and then show an error
          await new Promise(resolve => setTimeout(resolve, 5000));
          throw new Error("Server-side conversion is not yet implemented.");

        } catch (uploadError) {
          console.error('HEIC upload failed:', uploadError);
          setError('Failed to upload HEIC file for conversion. Please try again.');
          setLoading(false);
          return;
        }
      }

      // Optimize the image
      const optimized = await ImageOptimizer.optimizeImage(actualFile);
      setOptimizedImage(optimized);
      
      // Create preview URL from optimized image
      const reader = new FileReader();
      reader.onload = (e) => setImagePreviewUrl(e.target?.result as string);
      reader.readAsDataURL(optimized.file);

      // For new uploads, also save to user gallery
      if (source === 'upload' && user?.id) {
        try {
          await userGalleryService.uploadUserImage(actualFile, user.id);
        } catch (galleryError) {
          console.warn('Failed to save to gallery:', galleryError);
          // Continue with AI touchup even if gallery save fails
        }
      }

      setLoading(false);
      
      // Scroll to operations section if an existing image was selected
      if (source === 'existing' && operationSectionRef.current) {
        setTimeout(() => {
          operationSectionRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }, 300); // Small delay to ensure image is loaded and UI is updated
      }
    } catch (error) {
      console.error('Image processing failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to process image';

      if (errorMessage.startsWith('HEIC_CONVERSION_FAILED')) {
        setError(
          <span>
            HEIC/HEIF conversion is not supported in your browser. Please{' '}
            <a href="https://cloudconvert.com/heic-to-jpg" target="_blank" rel="noopener noreferrer" className="underline text-blue-600">
              convert your image to JPG
            </a>{' '}
            or try a different browser.
          </span>
        );
      } else {
        setError(errorMessage);
      }
      
      // Fallback to original file for unsupported formats
      const reader = new FileReader();
      reader.onload = (e) => setImagePreviewUrl(e.target?.result as string);
      reader.readAsDataURL(file);
      setLoading(false);
    }
  };

  const handleImageRemoved = () => {
    setSelectedImage(null);
    setOptimizedImage(null);
    cleanupBlobUrl(imagePreviewUrl);
    setImagePreviewUrl(null);
    setError(null);
    clearMask();
  };

  // Update canvas size when image changes
  useEffect(() => {
    if (imagePreviewUrl && canvasRef.current && imageRef.current) {
      const canvas = canvasRef.current;
      const img = imageRef.current;
      
      const updateCanvasSize = () => {
        const rect = img.getBoundingClientRect();
        
        console.log('🖼️ Image rect:', rect);
        console.log('🖼️ Image natural size:', { naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight });
        console.log('🖼️ Image current size:', { width: img.width, height: img.height });
        
        // Wait for image to have proper dimensions
        if (rect.width === 0 || rect.height === 0) {
          console.log('⚠️ Image not ready yet, scheduling retry...');
          setTimeout(updateCanvasSize, 100);
          return;
        }
        
        // Store existing mask data before resizing if any exists
        const ctx = canvas.getContext('2d');
        let existingMaskData: ImageData | null = null;
        
        if (ctx && canvas.width > 0 && canvas.height > 0) {
          try {
            existingMaskData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          } catch (e) {
            // Ignore errors when getting image data
          }
        }
        
        // Use the actual display size of the image
        const newWidth = Math.round(rect.width);
        const newHeight = Math.round(rect.height);
        
        console.log('🎯 Setting canvas size to:', { newWidth, newHeight });
        
        // Always update canvas size to ensure it matches
        canvas.width = newWidth;
        canvas.height = newHeight;
        canvas.style.width = `${newWidth}px`;
        canvas.style.height = `${newHeight}px`;
        
        // Clear the canvas after size change
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          
          // If we had existing mask data and the size changed, we need to clear the stored mask
          if (existingMaskData) {
            console.log('Canvas resized - clearing mask data');
            setMaskData(null);
          }
        }
        
        console.log('✅ Canvas updated:', { 
          canvasWidth: canvas.width, 
          canvasHeight: canvas.height, 
          cssWidth: canvas.style.width,
          cssHeight: canvas.style.height,
          imageRect: rect 
        });
      };
      
      // Force update after a delay to ensure image is rendered
      const forceUpdate = () => {
        setTimeout(() => {
          console.log('🔄 Force updating canvas size...');
          updateCanvasSize();
        }, 100);
      };
      
      // Update immediately if image is already loaded
      if (img.complete && img.naturalWidth > 0) {
        updateCanvasSize();
        forceUpdate(); // Also force update to be sure
      } else {
        // Wait for image to load
        img.onload = () => {
          updateCanvasSize();
          forceUpdate(); // Also force update after load
        };
      }
      
      // Also update on window resize for mobile orientation changes
      const handleResize = () => {
        setTimeout(updateCanvasSize, 100); // Small delay to ensure layout is updated
      };
      
      window.addEventListener('resize', handleResize);
      
      // For mobile, also listen to orientation changes
      const handleOrientationChange = () => {
        setTimeout(updateCanvasSize, 300); // Longer delay for orientation change
      };
      
      if (isMobile) {
        window.addEventListener('orientationchange', handleOrientationChange);
      }
      
      return () => {
        window.removeEventListener('resize', handleResize);
        if (isMobile) {
          window.removeEventListener('orientationchange', handleOrientationChange);
        }
      };
    }
  }, [imagePreviewUrl, isMobile]);

  // Auto-resize canvas when switching to erase/change modes
  useEffect(() => {
    if ((operationType === 'erase' || operationType === 'change') && imagePreviewUrl && canvasRef.current && imageRef.current) {
      const canvas = canvasRef.current;
      const img = imageRef.current;
      
      // Small delay to ensure the canvas is visible before resizing
      setTimeout(() => {
        const rect = img.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          canvas.width = Math.round(rect.width);
          canvas.height = Math.round(rect.height);
          canvas.style.width = `${Math.round(rect.width)}px`;
          canvas.style.height = `${Math.round(rect.height)}px`;
          
          // Clear any existing drawing
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
          }
          
          console.log('🔄 Auto-resized canvas for operation type:', operationType, { width: canvas.width, height: canvas.height });
        }
      }, 50);
    }
  }, [operationType, imagePreviewUrl]);

  useEffect(() => {
    return () => cleanupBlobUrl(resultImage);
  }, [resultImage]);

  // Scroll to result section when result image is available
  useEffect(() => {
    if (resultImage && resultSectionRef.current) {
      resultSectionRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, [resultImage]);

  const getCanvasPosition = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    
    let clientX: number, clientY: number;
    
    if ('touches' in e) {
      // Touch event - handle multiple touch points and prevent default
      e.preventDefault();
      const touch = e.touches[0] || e.changedTouches[0];
      if (!touch) return { x: 0, y: 0 };
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else {
      // Mouse event
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    // Calculate position relative to canvas
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const pos = {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
    
    // Debug position calculation (only for mobile)
    if (isMobile) {
      console.log('📍 Position calc:', {
        clientPos: { x: clientX, y: clientY },
        rectPos: { left: rect.left, top: rect.top },
        rectSize: { width: rect.width, height: rect.height },
        canvasSize: { width: canvas.width, height: canvas.height },
        scale: { x: scaleX, y: scaleY },
        finalPos: pos
      });
    }
    
    return pos;
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault(); // Prevent scrolling on touch devices
    setIsDrawing(true);
    const pos = getCanvasPosition(e);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      console.log('🎨 Started drawing at:', pos);
    } else {
      console.error('❌ Canvas context not available for drawing');
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    e.preventDefault(); // Prevent scrolling on touch devices
    
    // For mobile, handle multiple touch points more gracefully
    if ('touches' in e && e.touches.length > 1) {
      return; // Ignore multi-touch gestures
    }
    
    const pos = getCanvasPosition(e);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      ctx.lineTo(pos.x, pos.y);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
      // Adjust brush size for mobile if needed
      const adjustedBrushSize = isMobile ? Math.max(brushSize, 15) : brushSize;
      ctx.lineWidth = adjustedBrushSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    }
  };

  const stopDrawing = (e?: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (e) {
      e.preventDefault();
    }
    setIsDrawing(false);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      const newMaskData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      setMaskData(newMaskData);
      console.log('🎨 Stored mask data:', { width: canvas.width, height: canvas.height, hasData: !!newMaskData });
    } else {
      console.error('❌ Canvas or context not available for storing mask data');
    }
  };

  const clearMask = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setMaskData(null);
      console.log('🧹 Cleared mask data');
    }
  };

  const canvasToBlob = (): Promise<Blob> => new Promise(resolve => canvasRef.current?.toBlob(blob => blob && resolve(blob), 'image/png'));
  const fileToArrayBuffer = (file: File): Promise<ArrayBuffer> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
  const blobToBase64 = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const handleSubmit = async () => {
    if (isSubmittingRef.current || !selectedImage || !optimizedImage || !user?.id) {
      setError('Please upload an image first.');
      return;
    }
    
    // For erase and change operations, require mask
    if ((operationType === 'erase' || operationType === 'change') && !maskData) {
      console.log('❌ Mask validation failed:', { operationType, hasMaskData: !!maskData });
      setError('Please create a mask by drawing on the image.');
      return;
    } else if (operationType === 'erase' || operationType === 'change') {
      console.log('✅ Mask validation passed:', { operationType, hasMaskData: !!maskData });
    }
    
    // For change and clean_selected operations, require prompt
    if ((operationType === 'change' || operationType === 'clean_selected') && !prompt.trim()) {
      setError('Please enter a description.');
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);
    setError(null);
    
    const processingId = `ai-${operationType}-${Date.now()}`;
    setLastProcessingId(processingId);

    try {
      await checkTokenBalance(user);
      if ((user?.tokens || 0) < TOKENS_REQUIRED) {
        setError(`Not enough tokens. You need ${TOKENS_REQUIRED}.`);
        return;
      }

      // Upload optimized image to R2
      const r2Service = CloudflareR2Service.fromEnvironment();
      const uploadResult = await r2Service.uploadOptimizedImage(
        optimizedImage.file,
        processingId,
        user.id,
        operationType
      );

      // Handle different operation types
      let webhookUrl: string;
      let formData: FormData;
      
      if (operationType === 'clean_selected') {
        // For clean_selected, use furniture removal webhook
        webhookUrl = process.env.REACT_APP_N8N_WEBHOOK_URL_FURNITURE_CHANGE!;
        if (!webhookUrl) throw new Error('Furniture removal webhook URL not configured.');
        
        formData = new FormData();
        formData.append('operation_type', 'clean_selected');
        formData.append('processingId', processingId);
        formData.append('originalImageUrl', uploadResult.url);
        formData.append('userId', user.id);
        formData.append('clean_selected_prompt', prompt.trim());
      } else {
        // For erase and change, use mask-based approach
        const imageBlob = new Blob([await fileToArrayBuffer(optimizedImage.file)], { type: optimizedImage.file.type });
        const maskCanvasBlob = await canvasToBlob();
        
        formData = new FormData();
        formData.append('image', imageBlob, optimizedImage.file.name);
        formData.append('mask', maskCanvasBlob, 'mask.png');
        formData.append('imageBase64', await blobToBase64(imageBlob));
        formData.append('maskBase64', await blobToBase64(maskCanvasBlob));
        formData.append('processingId', processingId);
        formData.append('originalImageUrl', uploadResult.url);
        if (user?.id) formData.append('userId', user.id);
        if (operationType === 'change' && prompt.trim()) {
          formData.append('prompt', prompt.trim());
        }
        
        webhookUrl = operationType === 'erase' ? process.env.REACT_APP_N8N_WEBHOOK_URL_REMOVE! : process.env.REACT_APP_N8N_WEBHOOK_URL_CHANGE!;
        if (!webhookUrl) throw new Error(`${operationType} webhook URL not configured.`);
      }

      // Use different services based on operation type
      if (operationType === 'clean_selected') {
        // Use furniture removal service for clean_selected operations
        await furnitureRemovalService.createOperation(user.id, {
          processing_id: processingId,
          operation_type: 'clean_selected',
          clean_selected_prompt: prompt.trim(),
          original_image_url: uploadResult.url,
          tokens_spent: TOKENS_REQUIRED,
          webhook_url: webhookUrl,
        });
      } else {
        // Use AI touchup service for erase and change operations
        await aiTouchupService.createOperation(user.id, {
          processing_id: processingId,
          operation_type: operationType,
          prompt: operationType === 'change' ? prompt.trim() : undefined,
          original_image_url: uploadResult.url,
          mask_data: await blobToBase64(await canvasToBlob()),
          tokens_spent: TOKENS_REQUIRED,
          webhook_url: webhookUrl,
        });
      }

      if (!await tokenService.spendTokens(user.id, TOKENS_REQUIRED, `Edit`)) {
        throw new Error('Failed to spend tokens.');
      }
      updateUserTokens((user?.tokens || 0) - TOKENS_REQUIRED);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      const response = await fetch(webhookUrl, { method: 'POST', mode: 'cors', body: formData, signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) throw new Error(`Webhook failed: ${response.statusText}`);
      
      const result = await response.json();
      const imageUrl = result[0]?.response?.body[0]?.fullUrl || result[0]?.fullUrl || result.image || result.fullUrl;

      if(imageUrl){
        setResultImage(imageUrl);
        // Use appropriate service based on operation type
        if (operationType === 'clean_selected') {
          await furnitureRemovalService.markOperationCompleted(processingId, imageUrl);
        } else {
          await aiTouchupService.markOperationCompleted(processingId, imageUrl);
        }
        setSuccess(true);
      } else {
        setError("AI operation finished but no image was returned.");
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(errorMessage);
      // Use appropriate service based on operation type for error handling
      if (operationType === 'clean_selected') {
        furnitureRemovalService.markOperationFailed(processingId, errorMessage).catch(e => console.error(e));
      } else {
        aiTouchupService.markOperationFailed(processingId, errorMessage).catch(e => console.error(e));
      }
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
      // Refresh token balance after operation to show updated balance
      refreshTokenBalance(user);
    }
  };

  // Remove galleryImages since we're using AllGenerationsViewer now

  return (
    <div className="max-w-6xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🖌️ Edit Tool</h1>
          <p className="text-gray-600">Edit your images with AI</p>
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium">
              💰 Available: {currentTokenBalance} tokens | Required: {TOKENS_REQUIRED}
            </div>
            <button onClick={() => refreshTokenBalance(user)} disabled={isUpdatingTokens} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium">
              {isUpdatingTokens ? 'Refreshing...' : '🔄 Refresh'}
            </button>
          </div>
        </div>

        {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md"><p className="text-red-700">{error}</p></div>}
        {success && <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md"><p className="text-green-700">🎉 AI processing complete!</p></div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <ImageSelector
                title="1. Select Image"
                uploadButtonText="Upload New Image"
                onImageSelected={handleImageSelected}
                onImageRemoved={handleImageRemoved}
                selectedImage={selectedImage}
                acceptedFormats="image/jpeg,image/jpg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif"
                maxSizeMB={50}
                showExisting={true}
              />
            </div>
          </div>
          <div className="space-y-6">
            <div ref={operationSectionRef} className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">2. AI Operation</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <button onClick={() => setOperationType('erase')} className={`p-4 border-2 rounded-lg text-center ${operationType === 'erase' ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}>
                  <div className="font-medium">🗑️ Erase</div>
                  <div className="text-xs text-gray-500">Remove objects</div>
                </button>
                <button onClick={() => setOperationType('change')} className={`p-4 border-2 rounded-lg text-center ${operationType === 'change' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
                  <div className="font-medium">✏️ Change</div>
                  <div className="text-xs text-gray-500">Modify objects</div>
                </button>
                <button onClick={() => setOperationType('clean_selected')} className={`p-4 border-2 rounded-lg text-center ${operationType === 'clean_selected' ? 'border-yellow-500 bg-yellow-50' : 'border-gray-300'}`}>
                  <div className="font-medium">🪑 Clean</div>
                  <div className="text-xs text-gray-500">Remove furniture</div>
                </button>
              </div>
              {operationType === 'change' && (
                <textarea 
                  value={prompt} 
                  onChange={(e) => setPrompt(e.target.value)} 
                  placeholder="Describe what you want to change this area into" 
                  className="w-full p-2 border rounded-md mb-4" 
                  rows={2}
                />
              )}
              {operationType === 'clean_selected' && (
                <textarea 
                  value={prompt} 
                  onChange={(e) => setPrompt(e.target.value)} 
                  placeholder="Describe what you want to remove from this area (e.g., 'sofa', 'chair', 'table')" 
                  className="w-full p-2 border rounded-md mb-4" 
                  rows={2}
                />
              )}
              {operationType !== 'clean_selected' && (
                <>
                  <div className="my-4">
                    <label>Brush Size: {brushSize}px</label>
                    <input type="range" min="5" max="50" value={brushSize} onChange={(e) => setBrushSize(Number(e.target.value))} className="w-full"/>
                    {isMobile && (
                      <p className="text-xs text-gray-500 mt-1">
                        Mobile mode: Minimum brush size is 15px for better touch control
                      </p>
                    )}
                  </div>
                  <button onClick={clearMask} className="px-4 py-2 bg-gray-600 text-white rounded-md">Clear Mask</button>
                </>
              )}
              {imagePreviewUrl && operationType !== 'clean_selected' && (
                <div className="relative mt-4">
                  <p className="text-sm mb-2">
                    Draw a mask over the area to edit
                    {isMobile && <span className="text-blue-600"> (Touch to draw)</span>}
                  </p>
                  <div className="relative border" style={{ width: 'fit-content', height: 'fit-content' }}>
                    <img ref={imageRef} src={imagePreviewUrl} alt="Background" className="w-full h-auto block"/>
                    <canvas 
                      ref={canvasRef} 
                      className="absolute top-0 left-0 cursor-crosshair" 
                      onMouseDown={startDrawing} 
                      onMouseMove={draw} 
                      onMouseUp={stopDrawing} 
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      onTouchCancel={stopDrawing}
                      style={{ 
                        touchAction: 'none',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none',
                        pointerEvents: 'auto'
                      }}
                    />
                  </div>
                  {isMobile && (
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Tip: Use your finger to draw on the image above
                    </p>
                  )}
                </div>
              )}
              {imagePreviewUrl && operationType === 'clean_selected' && (
                <div className="relative mt-4">
                  <p className="text-sm mb-2">
                    Your image preview (furniture will be removed from the entire image based on your description)
                  </p>
                  <div className="relative border rounded-lg overflow-hidden">
                    <img src={imagePreviewUrl} alt="Preview" className="w-full h-auto max-h-96 object-contain"/>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    💡 No mask needed - describe what furniture to remove and AI will find and remove it
                  </p>
                </div>
              )}
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <button type="button" onClick={handleSubmit} disabled={loading || !optimizedImage || ((operationType === 'erase' || operationType === 'change') && !maskData) || currentTokenBalance < TOKENS_REQUIRED} className="w-full py-3 px-4 rounded-md text-white bg-blue-600 disabled:bg-gray-400">
                {loading ? 'Processing...' : `Submit (${TOKENS_REQUIRED} tokens)`}
              </button>
            </div>
          </div>
          {resultImage && imagePreviewUrl && (
            <div ref={resultSectionRef} className="md:col-span-2 mt-8">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">🎉 Before & After Comparison</h3>
                <div className="rounded-lg overflow-hidden border border-gray-200">
                  <ReactBeforeSliderComponent
                    firstImage={{
                      imageUrl: imagePreviewUrl,
                      alt: 'Original Image'
                    }}
                    secondImage={{
                      imageUrl: resultImage,
                      alt: 'AI Processed Result'
                    }}
                    delimiterColor="#3B82F6"
                    currentPercentPosition={50}
                    withResizeFeel={true}
                    feelsOnlyTheDelimiter={false}
                    delimiterIconStyles={{
                      width: '50px',
                      height: '50px',
                      backgroundSize: 'contain',
                      borderRadius: '50%',
                      backgroundImage: 'url("/assets/renovirai-logo.png")',
                      backgroundColor: 'white',
                      border: '2px solid #3B82F6'
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Drag the slider to compare before and after
                </p>
                <div className="mt-6 text-center">
                  <a href={resultImage} download={`${lastProcessingId}.webp`} className="bg-green-600 text-white px-6 py-3 rounded-md">
                    Download Result
                  </a>
                </div>
              </div>
            </div>
          )}

        
        </div>
        <AllGenerationsViewer />
      </div>
      
             <GenerationLoadingOverlay
         isVisible={loading}
         loadingSteps={LOADING_SEQUENCES.AI_TOUCHUP}
       />
    </div>
  );
}; 